<template>
  <div v-if="deviceOk" ref="layer" class="bloop-layer" aria-hidden="true"></div>
  <aside v-if="deviceOk && debug" class="bloop-debug" data-bloop-ignore>
    <p class="bloop-debug-title">Bloop debug</p>
    <pre>{{ summary }}</pre>
    <div class="bloop-debug-row">
      <button v-for="verb in verbs" :key="verb" type="button" @click="play(verb)">{{ verb }}</button>
    </div>
    <div class="bloop-debug-row">
      <button type="button" @click="circle">draw a circle</button>
      <button type="button" @click="wiggle">draw a wiggle</button>
      <button type="button" @click="copySession">copy session</button>
      <span>{{ copyNote }}</span>
    </div>
  </aside>
</template>

<script>
import { createMind } from './mind';

const BLOCKED = ['/minigame'];

function debugEnabled() {
  const search = new URLSearchParams(window.location.search);
  if (search.get('bloopDebug') === '1') return true;
  const hash = window.location.hash || '';
  const queryIndex = hash.indexOf('?');
  if (queryIndex === -1) return false;
  return new URLSearchParams(hash.slice(queryIndex + 1)).get('bloopDebug') === '1';
}

export default {
  name: 'BloopLayer',
  data() {
    return {
      deviceOk: false,
      debug: false,
      mind: null,
      status: null,
      copyNote: '',
      verbs: [
        'stay_still',
        'drift_along',
        'imitate_gesture',
        'imitate_on_element',
        'anticipate',
        'exaggerate',
        'flee',
        'invite',
        'lose_interest',
      ],
    };
  },
  computed: {
    blocked() {
      return BLOCKED.includes(this.$route.path);
    },
    summary() {
      if (!this.status) return 'Move the pointer, or use the buttons.';
      const decision = this.status.decision;
      if (!decision) {
        const gesture = this.status.observation && this.status.observation.current_gesture;
        const head = `${this.status.reason || 'local'} ${this.status.error || ''} calls ${this.status.calls}`;
        return gesture ? `${head}\n${JSON.stringify(gesture, null, 2)}` : head;
      }
      const details = decision.debug || {};
      const topVerbs = details.verb
        ? Object.entries(details.verb)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3)
          .map(([name, probability]) => `${name} ${Number(probability).toFixed(2)}`)
          .join('  ')
        : '';
      return [
        `${decision.verb}  ${decision.feeling}  certainty ${decision.certainty}  ${this.status.ms || '-'}ms`,
        topVerbs,
        `deliberate ${details.deliberate}  play ${details.playfulness}  directed ${details.directed_at_bloop}  repeating ${details.repeating}`,
        `gesture ${decision.labels && decision.labels.gesture}  response ${decision.labels && decision.labels.response}  glyph ${decision.glyph || '-'}`,
        `calls ${this.status.calls}${this.status.givenUp ? '  stopped' : ''}`,
      ].join('\n');
    },
  },
  watch: {
    blocked(next) {
      if (this.mind) this.mind.setBlocked(next);
    },
    '$route.path'() {
      if (!this.mind) return;
      this.$nextTick(() => this.mind.lookForSignal());
    },
  },
  mounted() {
    const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.debug = debugEnabled();
    this.deviceOk = fine && !reduce;
    if (!this.deviceOk) return;
    this.$nextTick(() => {
      if (!this.$refs.layer) return;
      this.mind = createMind({
        layer: this.$refs.layer,
        debug: this.debug,
        blocked: this.blocked,
        onStatus: (status) => {
          this.status = status;
        },
      });
      this.mind.start();
    });
  },
  beforeUnmount() {
    if (this.mind) this.mind.stop();
  },
  methods: {
    play(verb) {
      if (this.mind) this.mind.playVerb(verb);
    },
    circle() {
      if (this.mind) this.mind.injectCircle();
    },
    wiggle() {
      if (this.mind) this.mind.injectWiggle();
    },
    async copySession() {
      if (!this.mind) return;
      try {
        await navigator.clipboard.writeText(JSON.stringify(this.mind.session(), null, 2));
        this.copyNote = 'copied';
      } catch {
        this.copyNote = 'copy failed';
      }
    },
  },
};
</script>

<style>
.bloop-layer {
  position: fixed;
  inset: 0;
  z-index: 10050;
  pointer-events: none;
}

.bloop-cursor {
  position: absolute;
  left: 0;
  top: 0;
  width: 0;
  height: 0;
  opacity: 0;
  will-change: transform;
}

.bloop-cursor svg {
  display: none;
  position: absolute;
  left: 0;
  top: 0;
  overflow: visible;
}

.bloop-cursor[data-sprite="arrow"][data-platform="windows"] .arrow-windows,
.bloop-cursor[data-sprite="arrow"][data-platform="mac"] .arrow-mac,
.bloop-cursor[data-sprite="hand"] .hand,
.bloop-cursor[data-sprite="ibeam"] .ibeam {
  display: block;
}

.bloop-cursor .hand { left: -11px; top: -4px; }
.bloop-cursor .ibeam { left: -8px; top: -12px; }

.bloop-glyph {
  position: absolute;
  left: 16px;
  top: -16px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 15px;
  font-weight: 700;
  color: #fff;
  text-shadow: 0 0 2px #000, 0 1px 1px #000;
  opacity: 0;
}

.bloop-glyph.is-on { opacity: 1; }

.bloop-confetti {
  position: absolute;
  left: 0;
  top: 0;
  width: 6px;
  height: 9px;
  pointer-events: none;
}

.bloop-highlight {
  position: fixed;
  background: Highlight;
  opacity: 0.35;
  pointer-events: none;
}

.bloop-tooltip {
  position: fixed;
  transform: translate(-50%, -130%);
  background: rgba(0, 0, 0, 0.75);
  color: #fff;
  padding: 5px 10px;
  border-radius: 5px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.85rem;
  white-space: nowrap;
  opacity: 0;
}

.bloop-tooltip.is-on { opacity: 1; }

.bloop-press {
  outline: 1px solid var(--terminal-green, #39ff14);
  outline-offset: 2px;
}

.bloop-debug {
  position: fixed;
  left: 12px;
  bottom: 12px;
  z-index: 10060;
  width: min(560px, calc(100vw - 24px));
  max-height: 42vh;
  overflow: auto;
  padding: 10px 12px;
  background: rgba(10, 14, 20, 0.94);
  border: 1px solid #30363d;
  border-radius: 8px;
  color: #e6edf3;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  pointer-events: auto;
}

.bloop-debug-title {
  margin: 0 0 6px;
  color: #39ff14;
}

.bloop-debug pre {
  margin: 0 0 8px;
  white-space: pre-wrap;
  color: #8b949e;
}

.bloop-debug-row {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 6px;
}

.bloop-debug button {
  font-family: inherit;
  font-size: 10px;
  color: #39ff14;
  background: transparent;
  border: 1px solid #30363d;
  border-radius: 4px;
  padding: 3px 6px;
  cursor: pointer;
}

.bloop-debug button:hover {
  border-color: #39ff14;
}
</style>
